/**
 * decisionLines — pure derivation of the run-record page's Decisions list
 * (26742) from whichever of two sources the loader managed to fetch:
 *
 *   - `decisions` — the substrate's `get_run_decisions` payload (26730/26747),
 *     the authoritative source: resolved actor names, `surface`, and the
 *     notification-lifecycle rows;
 *   - `hitlEvents` — the existing `workflow_instance_hitl_events` history,
 *     the ONLY source this page had before this story and the one it always
 *     has, since it comes from `getWorkflowHitlHistory` (already merged).
 *
 * `decisions === null` is the explicit "the tool did not answer" signal —
 * distinct from `decisions.decisions === []` ("it answered; there is
 * nothing"). Both degrade to whatever `hitlEvents` alone can show, which is
 * why the loader treats an absent method, a rejected call, and a
 * `degraded: true` response identically: all three pass `null` here.
 *
 * Nothing in this module renders anything or knows about surfaces beyond the
 * raw wire values — `RunDecisionsSection` owns the final line-text assembly
 * (`${summary} · ${actorName} · via ${surfaceLabel}`) and every visual
 * decision (dimming, banners). This module only decides WHICH lines exist,
 * in WHAT order, carrying WHAT structured data.
 */
import type { HitlEvent } from "../components/WorkflowHitlHistorySection";
import { summarizeHitlResponse } from "../hitlSummary";
import type {
  GetRunDecisionsResponse,
  RunDecision,
  RunDecisionActor,
  RunDecisionSurface,
  RunNotification,
} from "./types";

export type DecisionLineActorKind = "person" | "system";
export type DecisionLineSource = "hitl" | "decision" | "notification";

export interface DecisionLine {
  /** Stable identity for React keys — also the dedupe/test handle. */
  key: string;
  /** ISO timestamp, or `null` when the source row has none. */
  ts: string | null;
  /** Human summary of WHAT happened — never the raw event_type or JSON. */
  summary: string;
  actorName: string | null;
  actorKind: DecisionLineActorKind;
  surface: RunDecisionSurface | string | null;
  source: DecisionLineSource;
  /** HITL-derived lines only — feeds the "what was shown ›" lightbox. */
  artifactsSnapshot?: string[];
  /** Notification lines only: `status !== "sent"`. */
  failed?: boolean;
  /** Notification lines only: an earlier row for the same decision_key that
   *  a later one superseded — kept (never dropped) so the audit trail is
   *  complete, but rendered dimmed. */
  dimmed?: boolean;
  /** Notification lines only, mirrored from the row for the banner copy. */
  sourceMessageLost?: boolean | null;
  /** The HITL event this line describes, when there is one — carried so the
   *  ledger can drill into the full prompt/response/artifacts via
   *  `HitlEventDetail` instead of the record maintaining a second, parallel
   *  list of the same events.
   *
   *  Present on `source: "hitl"` lines AND on the `source: "decision"` line
   *  that CONSUMED a HITL event during the person-row collapse below — the
   *  substrate row wins on actor/surface, but the detail must not be lost
   *  just because a richer row described the same moment. */
  hitlEvent?: HitlEvent;
}

/** "review_screenshots" -> "Review Screenshots"; "hitl.answered" -> "Hitl Answered". */
function prettyLabel(id: string | null | undefined): string | null {
  if (!id) return null;
  return id
    .split(/[._]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

/** The API hands us a parsed actor object, never the substrate's prefixed
 *  string — see `RunDecisionActor` in ./types. Tolerates a missing actor so a
 *  degraded payload renders as a system row rather than throwing. */
function isPersonActor(actor: RunDecisionActor | null | undefined): boolean {
  return actor?.kind === "person";
}

function lineFromHitlEvent(event: HitlEvent): DecisionLine {
  const stepLabel = prettyLabel(event.step_id);
  const summaryBase = summarizeHitlResponse(event.response, event.event_type);
  return {
    key: `hitl:${event.sequence}`,
    ts: event.answered_at ?? event.asked_at ?? null,
    summary: stepLabel ? `${stepLabel} — ${summaryBase}` : summaryBase,
    actorName:
      event.answered_by_name?.trim() || (event.answered_by ? "Resumer" : null),
    actorKind: "person",
    surface: null,
    source: "hitl",
    artifactsSnapshot: event.artifacts_snapshot,
    hitlEvent: event,
  };
}

function lineFromDecision(
  decision: RunDecision,
  index: number,
  hitlEvent?: HitlEvent,
): DecisionLine {
  const isPerson = isPersonActor(decision.actor);
  const summary = prettyLabel(decision.event_type) ?? "Decision recorded";
  return {
    // The row's ORIGINAL index in `decisions` always participates, so two rows
    // that share a trace_id (or share both event_type and ts, the fallback)
    // cannot collide. See lineFromNotification for why a collision matters.
    key: `decision:${decision.trace_id ?? `${decision.event_type}|${decision.ts}`}:${index}`,
    ts: decision.ts ?? null,
    summary,
    actorName: isPerson ? decision.actor?.name?.trim() || null : "ora",
    actorKind: isPerson ? "person" : "system",
    surface: decision.surface ?? null,
    source: "decision",
    ...(hitlEvent ? { hitlEvent } : {}),
  };
}

function lineFromNotification(
  notification: RunNotification,
  index: number,
  dimmed: boolean,
): DecisionLine {
  const failed = notification.status !== "sent";
  const summary = failed
    ? `Email not sent${notification.failure_reason ? ` — ${notification.failure_reason}` : ""}`
    : `Email sent${notification.recipient ? ` to ${notification.recipient}` : ""}`;
  return {
    // The index ALWAYS participates, not just when both ids are null. Multiple
    // notification rows sharing one `decision_key` is the retry/supersession
    // case this module exists to render (the `dimmed` flag below is for exactly
    // those rows) — keying on the shared id alone handed React duplicate keys
    // for the two rows it is most likely to see together, letting it carry a
    // superseded row's DOM state onto the newer one.
    key: `notification:${notification.decision_key ?? notification.decision_id ?? "none"}:${index}`,
    ts: notification.updated_at ?? notification.created_at ?? null,
    summary,
    actorName: "ora",
    actorKind: "system",
    surface: null,
    source: "notification",
    failed,
    dimmed,
    sourceMessageLost: notification.source_message_lost,
  };
}

/** `true` when two timestamps fall within `windowMs` of each other. Either
 *  side missing or unparseable is never "close" — the caller then falls back
 *  to a step/event_type match alone. */
function closeInTime(
  a: string | null,
  b: string | null,
  windowMs = 2000,
): boolean {
  if (!a || !b) return false;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  return Math.abs(ta - tb) <= windowMs;
}

/** Does this decision row describe the same moment as this HITL event? Ts
 *  within 2s AND (subject_id names the step OR the event_type matches) —
 *  either identifier alone is too weak (two HITL steps can answer within 2s
 *  of each other; two different runs' `hitl.answered` rows share the same
 *  event_type constantly). */
function describesSameMoment(decision: RunDecision, event: HitlEvent): boolean {
  const decisionTs = decision.ts ?? null;
  const eventTs = event.answered_at ?? event.asked_at ?? null;
  if (!closeInTime(decisionTs, eventTs)) return false;
  return (
    decision.subject_id === event.step_id ||
    decision.event_type === event.event_type
  );
}

/**
 * Build the ordered Decisions-section lines for a run.
 *
 * `decisions === null` (tool absent/degraded) → lines come from `hitlEvents`
 * alone, oldest first. Otherwise: HITL lines and PERSON-authored decision
 * rows describing the same moment collapse to ONE line (the substrate row
 * wins — it carries the resolved `actor.name`/`surface` the HITL table
 * doesn't); SYSTEM-authored decision rows never compete for a slot (a gate
 * verdict is never "the same" as a human answering a question) and always
 * get their own line; notification rows become their own lines, with all but
 * the most recent row per `decision_key` marked `dimmed`.
 */
export function buildDecisionLines(
  hitlEvents: HitlEvent[] | null | undefined,
  decisions: GetRunDecisionsResponse | null | undefined,
): DecisionLine[] {
  const events = hitlEvents ?? [];

  if (!decisions) {
    return events
      .slice()
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map(lineFromHitlEvent);
  }

  const hitlLines = events.map(lineFromHitlEvent);
  const decisionRows = decisions.decisions ?? [];

  // Both partitions keep each row's ORIGINAL index in `decisions`, so the two
  // lists cannot mint the same key as each other either.
  const personRows: { row: RunDecision; index: number }[] = [];
  const systemLines: DecisionLine[] = [];
  decisionRows.forEach((row, index) => {
    if (isPersonActor(row.actor)) {
      personRows.push({ row, index });
    } else {
      systemLines.push(lineFromDecision(row, index));
    }
  });

  const consumedHitlIndices = new Set<number>();
  const personLines: DecisionLine[] = [];
  for (const { row, index } of personRows) {
    const matchIdx = events.findIndex(
      (event, i) => !consumedHitlIndices.has(i) && describesSameMoment(row, event),
    );
    if (matchIdx >= 0) consumedHitlIndices.add(matchIdx);
    // Carry the consumed event onto the surviving line: the substrate row wins
    // on actor/surface, but the prompt/response/artifacts behind it are the
    // only copy there is, and dropping them would make a collapsed decision
    // less inspectable than an uncollapsed one.
    personLines.push(
      lineFromDecision(row, index, matchIdx >= 0 ? events[matchIdx] : undefined),
    );
  }

  const survivingHitlLines = hitlLines.filter(
    (_, i) => !consumedHitlIndices.has(i),
  );

  const notifications = decisions.notifications ?? [];
  const latestIndexByKey = new Map<string, number>();
  notifications.forEach((n, i) => {
    const key = n.decision_key ?? n.decision_id ?? String(i);
    const existingIdx = latestIndexByKey.get(key);
    if (existingIdx == null) {
      latestIndexByKey.set(key, i);
      return;
    }
    const currentTs = n.updated_at ?? n.created_at ?? "";
    const existingTs =
      notifications[existingIdx].updated_at ??
      notifications[existingIdx].created_at ??
      "";
    if (currentTs > existingTs) latestIndexByKey.set(key, i);
  });
  const notificationLines = notifications.map((n, i) => {
    const key = n.decision_key ?? n.decision_id ?? String(i);
    const isDimmed = latestIndexByKey.get(key) !== i;
    return lineFromNotification(n, i, isDimmed);
  });

  return [
    ...survivingHitlLines,
    ...personLines,
    ...systemLines,
    ...notificationLines,
  ].sort((a, b) => {
    const ta = a.ts ? Date.parse(a.ts) : 0;
    const tb = b.ts ? Date.parse(b.ts) : 0;
    return ta - tb;
  });
}

export default buildDecisionLines;
